// src/pages/Home.jsx
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SubscribeForm from '../components/SubscribeForm';
import Reveal from '../components/Reveal';

import heroImg from '../assets/images/hero-home.jpg';
import whoWeAreImg from '../assets/images/who-we-are.png';
import service1Img from '../assets/images/service1.png';
import service2Img from '../assets/images/service2.png';
import service3Img from '../assets/images/service3.jpg';
import blog1Img from '../assets/images/blog1.jpg';
import blog2Img from '../assets/images/blog2.jpg';
import blog3Img from '../assets/images/blog3.jpg';
import ctaImg from '../assets/images/cta-home.jpg';

function GlobeIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="10"/>
      <line x1="2" y1="12" x2="22" y2="12"/>
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
    </svg>
  );
}

function SatisfactionIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="10"/>
      <path d="M8 13s1.5 2 4 2 4-2 4-2"/>
      <line x1="9" y1="9" x2="9.01" y2="9"/>
      <line x1="15" y1="9" x2="15.01" y2="9"/>
    </svg>
  );
}

function SupportIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 18v-6a9 9 0 0 1 18 0v6"/>
      <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
    </svg>
  );
}

function ArrowRight({ color = 'currentColor' }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <line x1="5" y1="12" x2="19" y2="12"/>
      <polyline points="12 5 19 12 12 19"/>
    </svg>
  );
}

function ChevronRight({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points="9 18 15 12 9 6"/>
    </svg>
  );
}

const SERVICES = [
  {
    num: '01',
    title: 'Training and workforce development',
    desc: 'Our training programs are designed to build job-ready skills that support organizational staffing needs and individual career growth.',
    tags: ['QA', 'Cloud support', 'Operation & monitoring', 'Business analysis', 'Application development', 'AI automation'],
    img: service1Img,
    reverse: false,
  },
  {
    num: '02',
    title: 'Services',
    desc: 'We provide standardized IT services designed to keep systems stable reduce risk and ensure consistent delivery across operations.',
    tags: ['QA', 'Cloud support', 'Operation & monitoring', 'Business analysis', 'Application development', 'AI automation'],
    img: service2Img,
    reverse: true,
  },
  {
    num: '03',
    title: 'Staffing & workforce delivery model',
    desc: 'We operate as a workforce augmentation partner, providing trained professionals who work with client teams and are supported by internal delivery and quality processes.',
    tags: ['Engineers', 'Analyst', 'Designers', 'Support', 'QA', 'Contract-based', 'Project-based', 'Long-term staffing'],
    img: service3Img,
    reverse: false,
  },
];

const BLOGS = [
  {
    title: 'Reducing Cloud Costs',
    desc: 'An overview of practical approaches to cloud cost assessment, resource optimization, and ongoing cost monitoring without affecting existing systems.',
    img: blog1Img,
    slug: 'reducing-cloud-costs',
  },
  {
    title: 'Supporting System Modernization',
    desc: 'This post discusses common challenges in modernizing legacy systems while maintaining security, compliance, and service continuity.',
    img: blog2Img,
    slug: 'supporting-system-modernization',
  },
  {
    title: 'Building Workforce Continuity',
    desc: 'A look at how workforce planning, oversight, and structured delivery models help maintain stability across long-running IT initiatives.',
    img: blog3Img,
    slug: 'building-workforce-continuity',
  },
];

export default function Home() {
  return (
    <>
      <SEO
        title="Workforce & Managed IT Services"
        description="DistrictSourcingTech supports government, enterprise and individuals with workforce, managed IT services and training. Nationwide service reach with 99% client satisfaction."
        url="https://districtsourcingtech.com"
      />

      <Navbar variant="dark" />

      {/* ===== HERO ===== */}
      <section className="hero">
        <div className="hero__inner">
          <div className="hero__content">
            <div className="hero__text">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '809px' }}>
                <h1 className="hero__heading">
                  Workforce and managed IT services built for continuity
                </h1>
                <p className="hero__subtext">
                  DistrictSourcingTech supports government enterprise and individuals with workforce managed IT services and training
                </p>
              </div>
              <div className="hero__actions">
                <Link to="/contact" className="btn btn-light">Get started</Link>
                <Link to="/capabilities" className="btn btn-outline-white" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  Explore services <ArrowRight color="white" />
                </Link>
              </div>
            </div>
          </div>
          <div className="hero__image">
            <img src={heroImg} alt="Team collaborating in a modern office environment" loading="eager" />
          </div>
        </div>
      </section>

      {/* ===== TAGS SECTION ===== */}
      <section className="tags-section">
        {['Government & Public Sector', 'Private Sector & Enterprises', 'Individuals'].map((title, i) => {
          const descs = [
            'workforce augmentation, application support, modernization, training',
            'managed IT services, staffing, cloud optimization',
            'delivery-aligned training and job readiness',
          ];
          const nums = ['001', '002', '003'];
          return (
            <Reveal key={title} delay={i * 80} className="tag-item">
              <span className="tag-num">{nums[i]}</span>
              <div>
                <p className="tag-content-title">{title}</p>
                <p className="tag-content-desc" style={{ marginTop: '8px' }}>{descs[i]}</p>
              </div>
            </Reveal>
          );
        })}
      </section>

      {/* ===== WHO WE ARE ===== */}
      <section className="section">
        <div className="two-col" style={{ maxWidth: '1376px', margin: '0 auto' }}>
          <Reveal className="two-col__content">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <h2 className="title-2">Who we are</h2>
                <p className="body-lg" style={{ color: 'var(--token-desc)' }}>
                  DistrictSourcingTech helps organizations make better technology decisions through structured IT services and workforce support. We focus on reducing risk supporting operations and delivering predictable outcomes.
                </p>
              </div>
              <div className="stats-grid">
                {[
                  { icon: <GlobeIcon />, val: 'Nationwide', label: 'Service reach' },
                  { icon: <SatisfactionIcon />, val: '99%', label: 'Client satisfaction' },
                  { icon: <SupportIcon />, val: '24/7', label: 'Technical Support' },
                ].map(({ icon, val, label }, i) => (
                  <Reveal key={val} delay={i * 80} className="stat-card">
                    <div className="stat-icon" style={{ color: 'var(--primary-500)' }}>{icon}</div>
                    <div>
                      <p className="title-6" style={{ textAlign: 'center' }}>{val}</p>
                      <p className="body-sm" style={{ color: 'var(--token-desc)', textAlign: 'center' }}>{label}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
            <Link to="/about" className="btn btn-outline">About us</Link>
          </Reveal>
          <Reveal delay={200} className="two-col__image">
            <img src={whoWeAreImg} alt="Team working together on technology solutions" loading="lazy" />
          </Reveal>
        </div>
      </section>

      {/* ===== OUR SERVICES ===== */}
      <section className="section" style={{ paddingTop: '0' }}>
        <div style={{ maxWidth: '1376px', margin: '0 auto' }}>
          <Reveal className="section__header" style={{ marginBottom: '64px' }}>
            <h2 className="title-2">Our services</h2>
            <p className="body-lg" style={{ color: 'var(--token-desc)', maxWidth: '408px', textAlign: 'center' }}>
              Our services focus on keeping systems running reducing risk and supporting long-term operations.
            </p>
          </Reveal>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '80px' }}>
            {SERVICES.map((service) => (
              <Reveal key={service.num}>
                <div className={`two-col ${service.reverse ? 'two-col--reverse' : ''}`}>
                  <div className="two-col__image">
                    <img src={service.img} alt={service.title} loading="lazy" />
                  </div>
                  <div className="two-col__content" style={{ paddingLeft: service.reverse ? '0' : '32px', paddingRight: service.reverse ? '32px' : '0' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <h3 className="title-4">{service.title}</h3>
                          <span style={{ color: 'var(--primary-500)', fontSize: '14px', lineHeight: '21px', paddingBottom: '12px' }}>{service.num}</span>
                        </div>
                        <p className="body-lg" style={{ color: 'var(--token-desc)' }}>{service.desc}</p>
                      </div>
                      <div className="service-tags">
                        {service.tags.map(tag => <span key={tag} className="service-tag">{tag}</span>)}
                      </div>
                    </div>
                    <Link to="/capabilities" className="btn btn-outline" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Learn more <ArrowRight />
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== INSIGHTS / BLOG ===== */}
      <section className="section" style={{ paddingTop: '0', paddingBottom: '128px' }}>
        <div style={{ maxWidth: '1376px', margin: '0 auto' }}>
          <Reveal>
            <h2 className="title-2" style={{ marginBottom: '64px' }}>Insights</h2>
          </Reveal>
          <div className="blog-grid">
            {BLOGS.map((post, i) => (
              <Reveal key={post.slug} delay={i * 100}>
                <Link to={`/blog/${post.slug}`} className="blog-card" style={{ textDecoration: 'none' }}>
                  <div className="blog-card__image">
                    <img src={post.img} alt={post.title} loading="lazy" />
                  </div>
                  <div>
                    <h3 className="title-5 blog-card__title">{post.title}</h3>
                    <p className="body-lg blog-card__desc" style={{ color: 'var(--token-desc)', marginTop: '8px' }}>{post.desc}</p>
                  </div>
                  <div className="read-more">
                    <span>Read more</span>
                    <ChevronRight />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <Reveal as="div" className="cta-section">
        <div className="cta-inner">
          <div className="cta-content">
            <div>
              <h2 className="title-2" style={{ color: '#fff', marginBottom: '8px' }}>Ready to get started?</h2>
              <p className="body-lg" style={{ color: 'var(--shades-200)', maxWidth: '432px' }}>
                Reach out to discuss workforce support managed IT services or training needs. We will review your request and respond with clear next steps.
              </p>
            </div>
            <Link to="/contact" className="btn btn-light">Contact us</Link>
          </div>
          <div className="cta-image">
            <img src={ctaImg} alt="Professional team ready to help" loading="lazy" />
          </div>
        </div>
      </Reveal>

      {/* ===== SUBSCRIBE SECTION ===== */}
      <Reveal as="section" style={{ background: 'var(--shades-200)', padding: '80px 32px' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '24px', alignItems: 'center' }}>
          <div>
            <h2 className="title-4" style={{ marginBottom: '12px' }}>Stay updated</h2>
            <p className="body-lg" style={{ color: 'var(--token-desc)' }}>
              Get new job openings, insights, and company updates delivered to your inbox.
            </p>
          </div>
          <div style={{ width: '100%', maxWidth: '480px' }}>
            <SubscribeForm source="homepage" variant="light" />
          </div>
        </div>
      </Reveal>

      <Footer />
    </>
  );
}
